import { selectItemById } from '@data/selectors/itemSelectors';
import { useSelector } from 'react-redux';
import { Route, Routes, useNavigate, useParams } from 'react-router-dom';
import { useCallback, useEffect, useRef, useState } from 'react';
import UploadView from './UploadView';
import ItemView from './ItemView';
import axios, { CancelTokenSource } from 'axios';
import { FailedImage, UploadResult, UploadSelectOption } from '@shared/types';
import { imageUpload } from '@api/imageUpload';
import { useTranslation } from 'react-i18next';
import DefaultSpinner from '@components/ui/DefaultSpinner';
import { Flex } from '@kvdbil/components';
import { imageCompression } from '@utils/imageCompression';
import { Item } from '@shared/interfaces';
import { getUrlFromFile } from '@utils/image';
import { log } from '@shared/bugsnag';

const handleImageUpload = async ({
  file,
  item,
  mediaTypeOption,
  source,
  visible,
}: {
  file: File;
  item: Item;
  mediaTypeOption: UploadSelectOption;
  source: CancelTokenSource;
  visible: boolean;
}): Promise<UploadResult> => {
  let compressedFile: File | null;

  const getErrorResp = (message?: string): FailedImage => ({
    status: 'FAILED',
    name: file.name,
    size: file.size,
    type: file.type,
    error: new Error(message ? message : 'Image compression failed'),
  });

  try {
    compressedFile = await imageCompression(file);
  } catch (error: unknown) {
    return getErrorResp(error instanceof Error ? error?.message : undefined);
  }

  if (compressedFile) {
    const data = await imageUpload(
      compressedFile,
      item?.id,
      mediaTypeOption.value,
      source,
      visible
    );

    return data;
  } else {
    return getErrorResp();
  }
};

const logFailedUploads = (uploadedImages: UploadResult[]) => {
  // handling log failed uploads
  const failedUploads = Array.from(uploadedImages).filter(
    result => result.status === 'FAILED'
  ) as FailedImage[];

  if (failedUploads.length > 0) {
    log(
      new Error(
        `Image upload failed: ${failedUploads.length} / ${uploadedImages.length}`
      ),
      {
        severity: 'error',
        unhandled: true,
        logs: failedUploads.map(failedUpload => ({
          name: failedUpload.name,
          size: failedUpload.size,
          type: failedUpload.type,
          error: failedUpload.error.name,
          errorMessage: failedUpload.error.message,
        })),
      }
    );
  }
};

const ItemRoutes = () => {
  const { itemId } = useParams<{
    itemId: string;
  }>();
  const { t } = useTranslation();

  const navigate = useNavigate();

  const [mediaTypeOption, setMediaTypeOption] = useState<UploadSelectOption>({
    value: 'PRESENTATION',
    label: t('Presentation'),
  });

  const item = useSelector(selectItemById(itemId));
  // I will keep this incase we want ot implement a retry feature
  const fileListRef = useRef<FileList | null>(null);

  const [fileUrls, setFileUrls] = useState<string[]>([]);
  const [visible, changeVisibility] = useState<boolean>(true);

  const [cancelToken, setCancelToken] = useState<CancelTokenSource | null>(
    null
  );
  const [uploadedImages, setUploadedImages] = useState<UploadResult[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleOnChangeMediaType = useCallback((type: UploadSelectOption) => {
    setMediaTypeOption(type);
  }, []);

  const handleCancel = useCallback(() => {
    cancelToken?.cancel('Operation canceled by the user.');
    setDialogOpen(false);
  }, [cancelToken]);

  const uploadFiles = useCallback(
    async (files: FileList) => {
      if (!files || !item?.id) {
        return [];
      }

      const source = axios.CancelToken.source();
      setCancelToken(source);
      setUploadedImages([]);
      setDialogOpen(false);

      const imageUploads: UploadResult[] = [];

      for await (const file of Array.from(files)) {
        if (source.token.reason) {
          setUploadedImages(prev => [
            ...prev,
            { status: 'CANCELED', name: file.name },
          ]);
        } else {
          const result = await handleImageUpload({
            file,
            item,
            mediaTypeOption,
            source,
            visible,
          });

          setUploadedImages(prev => [...prev, result]);
          imageUploads.push(result);
        }
      }

      setCancelToken(null);
      setDialogOpen(true);

      return imageUploads;
    },
    [item, mediaTypeOption, visible]
  );

  const handleOnCLoseDialog = useCallback(() => {
    setDialogOpen(false);
  }, []);

  const handleOnOpenDialog = useCallback(() => {
    setDialogOpen(true);
  }, []);

  const handleFileSelection = useCallback(
    async (files: FileList | null) => {
      fileListRef.current = files;

      const images = await Promise.all(
        Array.from(files || []).map(file => getUrlFromFile(file))
      );
      setFileUrls(images.filter(Boolean) as string[]);

      if (!files) {
        return;
      }

      navigate('upload');
      const uploadedImages = await uploadFiles(files);

      logFailedUploads(uploadedImages);
    },
    [navigate, uploadFiles]
  );

  useEffect(() => {
    return () => {
      // Cancel the request when the component unmounts
      cancelToken?.cancel();
    };
  }, [cancelToken]);

  if (!item) {
    return (
      <Flex grow={1}>
        <DefaultSpinner />
      </Flex>
    );
  }

  return (
    <Routes>
      <Route
        path="/"
        element={
          <ItemView
            onFileSelected={handleFileSelection}
            selectedMediaTypeOption={mediaTypeOption}
            onChangeMediaType={handleOnChangeMediaType}
            visible={visible}
            changeVisibility={changeVisibility}
          />
        }
      />
      <Route
        path="upload"
        element={
          <UploadView
            fileUrls={fileUrls}
            uploadedImages={uploadedImages}
            onCancelUpload={handleCancel}
            dialogOpen={dialogOpen}
            onCLoseDialog={handleOnCLoseDialog}
            onOpenDialog={handleOnOpenDialog}
          />
        }
      />
    </Routes>
  );
};

export default ItemRoutes;
